import React, { Fragment, useState } from 'react';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { INotificationMenu } from './interfaces';
import {
  useGetMyNotifications,
  useGetMyUnreadNotificationCount,
} from '@reactQuery/notifications';
import { useOpenNotification } from '@hooks/useOpenNotification';
import { ROUTES } from '@routes/routes';
import { NotificationMenuItem } from './components/NotificationMenuItem';

export const NotificationMenu: React.FC<INotificationMenu> = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openNotification = useOpenNotification();
  const { data: unreadCount = 0 } = useGetMyUnreadNotificationCount();
  const { data: notifications = [] } = useGetMyNotifications(5);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const closeMenu = (): void => {
    setAnchorEl(null);
  };

  const ariaLabel =
    unreadCount > 0
      ? t('notifications:bell.unread', { count: unreadCount })
      : t('notifications:bell.empty');

  return (
    <Fragment>
      <IconButton
        aria-label={ariaLabel}
        color="default"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          width: 40,
          height: 40,
        }}
      >
        <Badge
          badgeContent={unreadCount}
          invisible={unreadCount === 0}
          max={9}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.6rem',
              height: '16px',
              minWidth: '16px',
            },
          }}
        >
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'calc(100vh - 80px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              mt: 0.5,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 12,
                height: 12,
                backgroundColor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                boxShadow: '-1px -1px 1px rgba(0, 0, 0, 0.08)',
                zIndex: 0,
              },
            },
          },
          list: {
            sx: {
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flex: '1 1 auto',
              minHeight: 0,
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
          <Typography component="h2" variant="subtitle1" fontWeight={700}>
            {t('notifications:title')}
          </Typography>
        </Box>
        <Divider sx={{ flexShrink: 0 }} />
        <Box
          sx={{
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          {notifications.length === 0 ? (
            <MenuItem disabled>{t('notifications:empty.title')}</MenuItem>
          ) : (
            notifications.map((notification) => (
              <NotificationMenuItem
                key={notification.id}
                notification={notification}
                onClick={(selected) => {
                  closeMenu();
                  void openNotification(selected);
                }}
              />
            ))
          )}
        </Box>
        <Divider sx={{ flexShrink: 0 }} />
        <MenuItem
          sx={{ flexShrink: 0 }}
          onClick={() => {
            closeMenu();
            navigate(ROUTES.profile.notifications);
          }}
        >
          <Typography variant="body2">{t('notifications:viewAll')}</Typography>
        </MenuItem>
      </Menu>
    </Fragment>
  );
};
